import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useWhatsAppSocket } from "./WhatsAppSocketContext";
import { useNotification } from "./NotificationContext";
import moment from "moment";

const WhatsAppContext = createContext(null);

export const useWhatsApp = () => {
  return useContext(WhatsAppContext);
};

export const WhatsAppProvider = ({ children }) => {
  // Socket and notification contexts
  const socket = useWhatsAppSocket();
  const { showNotification } = useNotification();

  // WhatsApp connection states
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [filteredChats, setFilteredChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and statistics states
  const [currentFilter, setCurrentFilter] = useState("all");
  const [chatStatistics, setChatStatistics] = useState({
    totalFavorites: 0,
    totalPinned: 0,
    totalUnread: 0,
    totalChats: 0,
  });

  // Incremental loading states
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // References to prevent recreation issues
  const selectedChatRef = useRef(selectedChat);
  const messagesRef = useRef(messages);

  // Update references
  selectedChatRef.current = selectedChat;
  messagesRef.current = messages;

  // Escuchar eventos de logout del sistema principal para limpiar estado
  useEffect(() => {
    const handleMainLogout = () => {
      
      // Resetear todo el estado de WhatsApp
      setConnected(false);
      setAuthenticated(false);
      setQrCode(null);
      setChats([]);
      setSelectedChat(null);
      setMessages({});
      setSearchQuery("");
      setSearchResults([]);
      setFilteredChats([]);
      setLoading(true);
      setError(null);
      setCurrentFilter("all");
      setChatStatistics({
        totalFavorites: 0,
        totalPinned: 0,
        totalUnread: 0,
        totalChats: 0,
      });
      setHasMoreMessages(true);
      setIsLoadingMore(false);
      
      // Limpiar localStorage de WhatsApp
      localStorage.removeItem("whatsapp_authenticated");
      localStorage.removeItem("whatsapp_selected_chat");
    };

    window.addEventListener('auth:logout', handleMainLogout);

    return () => {
      window.removeEventListener('auth:logout', handleMainLogout);
    };
  }, []);

  // Implement emitWithAck on existing socket
  useEffect(() => {
    if (socket && !socket.emitWithAck) {
      socket.emitWithAck = function (event, data) {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error(`Timeout esperando respuesta para evento: ${event}`));
          }, 15000);

          this.emit(event, data, (response) => {
            clearTimeout(timeout);
            if (response && response.error) {
              reject(new Error(response.message || response.error));
            } else {
              resolve(response);
            }
          });
        });
      };
    }
  }, [socket]);

  // Monitor socket connection
  useEffect(() => {
    if (!socket) {
      setConnected(false);
      return;
    }

    setConnected(socket.connected);
    setError(null);

    const handleSocketConnect = () => {
      setConnected(true);
      setError(null);
    };

    const handleSocketDisconnect = (reason) => {
      setConnected(false);
      setError("Desconectado del servidor");
    };

    socket.on("connect", handleSocketConnect);
    socket.on("disconnect", handleSocketDisconnect);

    // Verify WhatsApp authentication if socket is connected
    if (socket.connected && localStorage.getItem("whatsapp_authenticated") === "true") {
      socket.emit("checkAuthentication");
    }

    return () => {
      socket.off("connect", handleSocketConnect);
      socket.off("disconnect", handleSocketDisconnect);
    };
  }, [socket]);

  // Load authenticated state from localStorage
  useEffect(() => {
    const savedAuth = localStorage.getItem("whatsapp_authenticated");
    if (savedAuth === "true") {
      setAuthenticated(true);
      setLoading(false);
    }

    setSelectedChat(null);
    localStorage.removeItem("whatsapp_selected_chat");

    if (socket && socket.connected) {
      socket.emit("get-whatsapp-status");
      
      if (savedAuth === "true") {
        setTimeout(() => {
          socket.emit("get-chats");
        }, 500);
      }
    }
  }, [socket]);

  // Function to determine media type from mimetype
  const getMediaTypeFromMimetype = useCallback((mimetype) => {
    if (!mimetype) return "unknown";

    if (mimetype.startsWith("image/")) {
      if (mimetype === "image/webp") return "sticker";
      return "image";
    }

    if (mimetype.startsWith("video/")) return "video";
    if (mimetype.startsWith("audio/")) return "audio";

    const documentTypes = {
      "application/pdf": "pdf",
      "application/msword": "doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        "docx",
      "application/vnd.ms-excel": "xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        "xlsx",
      "application/vnd.ms-powerpoint": "ppt",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        "pptx",
      "text/plain": "txt",
      "application/json": "json",
      "application/zip": "zip",
      "application/x-rar-compressed": "rar",
    };

    return documentTypes[mimetype] || "document";
  }, []);

  // Configure WhatsApp events with OTP support
  useEffect(() => {
    if (!socket) return;

    // Event handler: QR code reception
    const handleQR = (data) => {
      const qrImage = typeof data === 'string' ? data : (data.qrImage || data.qr || data);

      if (qrImage) {
        setQrCode(qrImage);
        setLoading(false);
        setError(null);
        setAuthenticated(false);

        showNotification({
          message: "Código QR generado. Escanéalo con tu teléfono.",
          severity: "info",
          autoHideDuration: 5000,
        });
      }
    };

    // Event handler: WhatsApp status management
    const handleWhatsAppStatus = (statusData) => {
      if (statusData.hasQR && statusData.qr) {
        setQrCode(statusData.qr);
        setLoading(false);
        setError(null);
        setAuthenticated(false);
        localStorage.removeItem("whatsapp_authenticated");
      } else if (statusData.isReady && statusData.clientInfo) {
        setAuthenticated(true);
        setQrCode(null);
        setLoading(false);
        setError(null);
        localStorage.setItem("whatsapp_authenticated", "true");

        showNotification({
          message: "WhatsApp conectado exitosamente",
          severity: "success",
          autoHideDuration: 3000,
        });

        if (socket) {
          setTimeout(() => socket.emit("get-chats"), 500);
        }
      } else if (statusData.error) {
        setError(statusData.error || "Error de conexión");
        setLoading(false);
        setQrCode(null);
        setAuthenticated(false);
        localStorage.removeItem("whatsapp_authenticated");
      } else {
        setLoading(true);
        setError(null);
      }
    };

    // Event handler: chat list reception
    const handleChats = (chatList) => {
      if (!Array.isArray(chatList)) {
        showNotification({
          message: "Error al cargar los chats",
          severity: "error",
          autoHideDuration: 5000,
        });
        return;
      }

      setChats(chatList);
      setFilteredChats(chatList);
      setLoading(false);

      const stats = {
        totalChats: chatList.length,
        totalFavorites: chatList.filter((chat) => chat.isFavorite).length,
        totalPinned: chatList.filter((chat) => chat.isPinned).length,
        totalUnread: chatList.filter((chat) => chat.unreadCount > 0).length,
      };
      setChatStatistics(stats);
    };

    // Event handler: chat messages reception
    const handleChatMessages = ({ chatId, messages: chatMessages, hasMore }) => {
      if (!Array.isArray(chatMessages)) {
        showNotification({
          message: "Error al cargar los mensajes del chat",
          severity: "error",
          autoHideDuration: 5000,
        });
        return;
      }

      setMessages((prev) => ({
        ...prev,
        [chatId]: chatMessages,
      }));

      setHasMoreMessages(hasMore || false);

      if (socket) {
        const messagesWithMedia = chatMessages.filter(
          (msg) => msg.hasMedia && msg.mediaStatus === "needsLoading"
        );

        if (messagesWithMedia.length > 0) {
          const priorityMessages = messagesWithMedia.slice(0, 5);

          priorityMessages.forEach((message, index) => {
            setTimeout(() => {
              setMessages((prev) => {
                const currentMessages = prev[chatId] || [];
                return {
                  ...prev,
                  [chatId]: currentMessages.map((msg) =>
                    msg.id === message.id
                      ? { ...msg, mediaStatus: "loading" }
                      : msg
                  ),
                };
              });

              socket.emit(
                "getMessageMedia",
                {
                  messageId: message.id,
                  mediaMessageRef: message.mediaMessageRef,
                },
                (response) => {
                  if (response && !response.error) {
                    setMessages((prev) => {
                      const currentMessages = prev[chatId] || [];
                      return {
                        ...prev,
                        [chatId]: currentMessages.map((msg) =>
                          msg.id === message.id
                            ? {
                                ...msg,
                                mediaData: response,
                                mediaStatus: "loaded",
                                mediaType:
                                  response.type ||
                                  getMediaTypeFromMimetype(response.mimetype),
                              }
                            : msg
                        ),
                      };
                    });
                  } else {
                    setMessages((prev) => {
                      const currentMessages = prev[chatId] || [];
                      return {
                        ...prev,
                        [chatId]: currentMessages.map((msg) =>
                          msg.id === message.id
                            ? {
                                ...msg,
                                mediaStatus: "error",
                                mediaError:
                                  response?.message ||
                                  "Error al cargar multimedia",
                              }
                            : msg
                        ),
                      };
                    });
                  }
                }
              );
            }, index * 500);
          });

          if (messagesWithMedia.length > 5) {
            setTimeout(() => {
              messagesWithMedia.slice(5).forEach((message, index) => {
                setTimeout(() => {
                  setMessages((prev) => {
                    const currentMsg = prev[chatId]?.find(
                      (m) => m.id === message.id
                    );
                    if (
                      !currentMsg ||
                      currentMsg.mediaStatus !== "needsLoading"
                    ) {
                      return prev;
                    }

                    const currentMessages = prev[chatId] || [];
                    return {
                      ...prev,
                      [chatId]: currentMessages.map((msg) =>
                        msg.id === message.id
                          ? { ...msg, mediaStatus: "loading" }
                          : msg
                      ),
                    };
                  });

                  socket.emit(
                    "getMessageMedia",
                    {
                      messageId: message.id,
                      mediaMessageRef: message.mediaMessageRef,
                    },
                    (response) => {
                      if (response && !response.error) {
                        setMessages((prev) => {
                          const currentMessages = prev[chatId] || [];
                          return {
                            ...prev,
                            [chatId]: currentMessages.map((msg) =>
                              msg.id === message.id
                                ? {
                                    ...msg,
                                    mediaData: response,
                                    mediaStatus: "loaded",
                                    mediaType:
                                      response.type ||
                                      getMediaTypeFromMimetype(
                                        response.mimetype
                                      ),
                                  }
                                : msg
                            ),
                          };
                        });
                      } else {
                        setMessages((prev) => {
                          const currentMessages = prev[chatId] || [];
                          return {
                            ...prev,
                            [chatId]: currentMessages.map((msg) =>
                              msg.id === message.id
                                ? {
                                    ...msg,
                                    mediaStatus: "error",
                                    mediaError:
                                      response?.message ||
                                      "Error al cargar multimedia",
                                  }
                                : msg
                            ),
                          };
                        });
                      }
                    }
                  );
                }, index * 1000);
              });
            }, 5000);
          }
        }
      }
    };

    // Event handler: new message reception
    const handleMessage = async (message) => {
      if (message.hasMedia) {
        try {
          message.mediaStatus = "loading";

          if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
            setMessages((prev) => {
              const currentMessages = prev[selectedChatRef.current.id] || [];
              const messageExists = currentMessages.some(
                (m) => m.id === message.id
              );

              if (messageExists) {
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id ? { ...m, mediaStatus: "loading" } : m
                  ),
                };
              } else {
                return {
                  ...prev,
                  [selectedChatRef.current.id]: [...currentMessages, message],
                };
              }
            });
          }

          const getMediaWithRetry = async (retries = 3) => {
            try {
              return new Promise((resolve, reject) => {
                socket.emit(
                  "getMessageMedia",
                  { messageId: message.id },
                  (response) => {
                    if (response && response.error) {
                      reject(new Error(response.message || response.error));
                    } else {
                      resolve(response);
                    }
                  }
                );

                setTimeout(
                  () => reject(new Error("Timeout getting media")),
                  15000
                );
              });
            } catch (error) {
              if (retries > 0) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
                return getMediaWithRetry(retries - 1);
              }
              throw error;
            }
          };

          try {
            const mediaData = await getMediaWithRetry();

            message.mediaData = mediaData;
            message.mediaStatus = "loaded";
            message.mediaType = mediaData.type || getMediaTypeFromMimetype(mediaData.mimetype);

            if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
              setMessages((prev) => {
                const currentMessages = prev[selectedChatRef.current.id] || [];
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id
                      ? {
                          ...m,
                          mediaData,
                          mediaStatus: "loaded",
                          mediaType:
                            mediaData.type ||
                            getMediaTypeFromMimetype(mediaData.mimetype),
                        }
                      : m
                  ),
                };
              });
            }
          } catch (mediaError) {
            message.mediaStatus = "error";
            message.mediaError = mediaError.message;

            if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
              setMessages((prev) => {
                const currentMessages = prev[selectedChatRef.current.id] || [];
                return {
                  ...prev,
                  [selectedChatRef.current.id]: currentMessages.map((m) =>
                    m.id === message.id
                      ? {
                          ...m,
                          mediaStatus: "error",
                          mediaError: mediaError.message,
                        }
                      : m
                  ),
                };
              });
            }
          }
        } catch (error) {
          message.mediaStatus = "error";
          message.mediaError = error.message;
        }
      }

      if (selectedChatRef.current && message.chatId === selectedChatRef.current.id) {
        setMessages((prev) => {
          const currentMessages = prev[selectedChatRef.current.id] || [];
          const tempIndex = currentMessages.findIndex(
            (m) =>
              m.body === message.body &&
              m.id.startsWith("temp_") &&
              m.fromMe === true
          );

          if (tempIndex >= 0) {
            const updatedMessages = [...currentMessages];
            updatedMessages[tempIndex] = {
              ...message,
              mediaData:
                message.mediaData || updatedMessages[tempIndex].mediaData,
              mediaStatus:
                message.mediaStatus || updatedMessages[tempIndex].mediaStatus,
            };
            return {
              ...prev,
              [selectedChatRef.current.id]: updatedMessages,
            };
          } else if (!currentMessages.some((m) => m.id === message.id)) {
            return {
              ...prev,
              [selectedChatRef.current.id]: [...currentMessages, message],
            };
          }

          return prev;
        });
      }

      setChats((prev) => {
        const updatedChats = prev.map((chat) => {
          if (chat.id === message.chatId) {
            return {
              ...chat,
              lastMessage: {
                body: message.hasMedia
                  ? message.mediaType
                    ? `[${message.mediaType}]`
                    : "[Media]"
                  : message.body,
                fromMe: message.fromMe,
              },
              timestamp: message.timestamp,
              unreadCount:
                selectedChatRef.current &&
                selectedChatRef.current.id === chat.id
                  ? 0
                  : (chat.unreadCount || 0) + 1,
            };
          }
          return chat;
        });

        return updatedChats.sort((a, b) => {
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return moment(b.timestamp).diff(moment(a.timestamp));
        });
      });
    };

    // Event handler: message status change (ACK updates)
    const handleMessageAck = ({ messageId, chatId, status, ack, fromMe }) => {
      setMessages((prevMessages) => {
        const updatedMessages = { ...prevMessages };
        const chatMessages = updatedMessages[chatId] || [];

        if (chatMessages.length === 0) {
          return prevMessages;
        }

        updatedMessages[chatId] = chatMessages.map((msg) => {
          if (msg.id === messageId) {
            return {
              ...msg,
              status,
              ack,
              isTemp: false,
            };
          }

          if (msg.id.startsWith("temp_") && msg.fromMe && fromMe) {
            const msgTime = moment(msg.timestamp);
            const now = moment();
            const isRecent = now.diff(msgTime, "seconds") < 60;

            const pendingTempCount = chatMessages.filter(
              (m) => m.id.startsWith("temp_") && m.fromMe && !m.confirmedId
            ).length;

            if (isRecent && pendingTempCount === 1 && !msg.confirmedId) {
              return {
                ...msg,
                id: messageId,
                status,
                ack,
                confirmedId: messageId,
                isTemp: false,
              };
            }
          }

          return msg;
        });

        return updatedMessages;
      });

      setChats((prevChats) => {
        return prevChats.map((chat) => {
          if (chat.id === chatId && chat.lastMessage && chat.lastMessage.fromMe) {
            return {
              ...chat,
              lastMessage: {
                ...chat.lastMessage,
                status,
              },
            };
          }
          return chat;
        });
      });
    };

    // Event handler: search results
    const handleSearchResults = (results) => {
      setSearchResults(results);
      setFilteredChats(results);
    };

    // Event handler: incremental message loading
    const handleMoreMessagesLoaded = ({
      chatId,
      messages: olderMessages,
      hasMore,
    }) => {
      setMessages((prev) => {
        const existingMessages = prev[chatId] || [];
        const combinedMessages = [...olderMessages, ...existingMessages];

        // Remove duplicates based on ID
        const uniqueMessages = combinedMessages.filter(
          (message, index, array) =>
            array.findIndex((m) => m.id === message.id) === index
        );

        return {
          ...prev,
          [chatId]: uniqueMessages,
        };
      });

      setHasMoreMessages(hasMore);
      setIsLoadingMore(false);
    };

    // Event handler: chat favorite toggle
    const handleChatFavoriteToggled = ({ chatId, isFavorite }) => {
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId ? { ...chat, isFavorite } : chat
        )
      );

      // Update statistics
      setChatStatistics((prev) => ({
        ...prev,
        totalFavorites: prev.totalFavorites + (isFavorite ? 1 : -1),
      }));

      showNotification({
        message: `Chat ${isFavorite ? "añadido a" : "removido de"} favoritos`,
        severity: "success",
        autoHideDuration: 2000,
      });
    };

    // Event handler: chat statistics
    const handleChatStatistics = (stats) => {
      setChatStatistics(stats);
    };

    // Event handler: chat selection cleared
    const handleChatSelectionCleared = () => {
      setSelectedChat(null);
      localStorage.removeItem("whatsapp_selected_chat");
    };

    // Event handler: general error
    const handleError = (errorData) => {
      const errorMessage = typeof errorData === "string"
          ? errorData
          : errorData.message || errorData.details || "Error desconocido";

      setError(errorMessage);
      setLoading(false);
      setAuthenticated(false);
      setQrCode(null);
      localStorage.removeItem("whatsapp_authenticated");

      showNotification({
        message: `Error de WhatsApp: ${errorMessage}`,
        severity: "error",
        autoHideDuration: 8000,
      });
    };

    // Event handler: disconnect
    const handleDisconnect = (disconnectData) => {

      setAuthenticated(false);
      setQrCode(null);
      setChats([]);
      setSelectedChat(null);
      setMessages({});
      localStorage.removeItem("whatsapp_authenticated");

      const reason = disconnectData.reason || "Conexión perdida";
      setError(`Desconectado: ${reason}`);

      showNotification({
        message: `WhatsApp desconectado: ${reason}`,
        severity: "warning",
        autoHideDuration: 5000,
      });
    };

    // Register all event listeners
    socket.on("qr-code", handleQR);
    socket.on("whatsapp-status", handleWhatsAppStatus);
    socket.on("whatsapp-ready", () => {
      setAuthenticated(true);
      setQrCode(null);
      setLoading(false);
      setError(null);
      localStorage.setItem("whatsapp_authenticated", "true");
      
      showNotification({
        message: "WhatsApp conectado exitosamente",
        severity: "success",
        autoHideDuration: 3000,
      });
      
      if (socket) {
        setTimeout(() => socket.emit("get-chats"), 500);
      }
    });
    socket.on("whatsapp-authenticated", () => {
      setAuthenticated(true);
      setLoading(false);
    });
    socket.on("chats-loaded", handleChats);
    socket.on("chat-messages-loaded", handleChatMessages);
    socket.on("whatsapp:message", handleMessage);
    socket.on("message-received", handleMessage);
    socket.on("message-sent", handleMessage);
    socket.on("message-ack", handleMessageAck);
    socket.on("message-status-updated", handleMessageAck);
    socket.on("whatsapp:message_ack", handleMessageAck);
    socket.on("chat-updated", (data) => {
      
      setChats((prevChats) => {
        const updatedChats = prevChats.map((chat) => {
          if (chat.id === data.chatId) {
            return {
              ...chat,
              lastMessage: data.lastMessage,
              lastMessageTime: data.timestamp,
              timestamp: data.timestamp
            };
          }
          return chat;
        });
        
        return updatedChats.sort((a, b) => {
          const timeA = a.lastMessageTime || a.timestamp || 0;
          const timeB = b.lastMessageTime || b.timestamp || 0;
          return timeB - timeA;
        });
      });
    });
    socket.on("searchResults", handleSearchResults);
    socket.on("moreMessagesLoaded", handleMoreMessagesLoaded);
    socket.on("chatFavoriteToggled", handleChatFavoriteToggled);
    socket.on("chatStatistics", handleChatStatistics);
    socket.on("chatSelectionCleared", handleChatSelectionCleared);
    socket.on("error", handleError); // Backend emite 'error'
    socket.on("whatsapp:disconnected", handleDisconnect);
    socket.on("whatsapp-disconnected", handleDisconnect);
    socket.on("whatsapp-disconnect", handleDisconnect);
    socket.on("message-sent-confirmation", (data) => {
      // Confirmation handled via other events
    });

    return () => {
      socket.off("qr-code", handleQR);
      socket.off("whatsapp-status", handleWhatsAppStatus);
      socket.off("whatsapp-ready");
      socket.off("whatsapp-authenticated");
      socket.off("chats-loaded", handleChats);
      socket.off("chat-messages-loaded", handleChatMessages);
      socket.off("whatsapp:message", handleMessage);
      socket.off("message-received", handleMessage);
      socket.off("message-sent", handleMessage);
      socket.off("message-ack", handleMessageAck); // Limpiar listener correcto
      socket.off("message-status-updated", handleMessageAck);
      socket.off("whatsapp:message_ack", handleMessageAck);
      socket.off("chat-updated");
      socket.off("searchResults", handleSearchResults);
      socket.off("moreMessagesLoaded", handleMoreMessagesLoaded);
      socket.off("chatFavoriteToggled", handleChatFavoriteToggled);
      socket.off("chatStatistics", handleChatStatistics);
      socket.off("chatSelectionCleared", handleChatSelectionCleared);
      socket.off("error", handleError);
      socket.off("whatsapp:disconnected", handleDisconnect);
      socket.off("whatsapp-disconnected", handleDisconnect);
      socket.off("whatsapp-disconnect", handleDisconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, getMediaTypeFromMimetype]);

  // Select a chat
  const selectChat = useCallback(
    (chat) => {
      setSelectedChat(chat);
      localStorage.setItem("whatsapp_selected_chat", chat.id);

      setHasMoreMessages(true);
      setIsLoadingMore(false);

      setChats((prev) =>
        prev.map((c) => (c.id === chat.id ? { ...c, unreadCount: 0 } : c))
      );

      if (socket) {
        socket.emit("set-active-chat", { chatId: chat.id });
        socket.emit("get-chat-messages", { chatId: chat.id, limit: 50 });
      }
    },
    [socket]
  );

  // Clear chat selection
  const clearChatSelection = useCallback(() => {
    if (socket) {
      socket.emit("clearChatSelection");
    }
    setSelectedChat(null);
    setHasMoreMessages(true);
    setIsLoadingMore(false);
    localStorage.removeItem("whatsapp_selected_chat");
  }, [socket]);

  // Send message with immediate local update
  const sendMessage = useCallback(
    (text) => {
      if (socket && selectedChat) {
        const tempId = `temp_${Date.now()}`;

        const tempMessage = {
          id: tempId,
          uuid: tempId,
          body: text,
          timestamp: moment().format("YYYY-MM-DD HH:mm:ss"),
          fromMe: true,
          status: "PENDING",
          authorName: "Tú",
          isTemp: true,
          sentTime: Date.now(),
        };

        setMessages((prev) => {
          const currentMessages = prev[selectedChat.id] || [];
          return {
            ...prev,
            [selectedChat.id]: [...currentMessages, tempMessage],
          };
        });

        setChats((prev) => {
          const updatedChats = prev.map((chat) => {
            if (chat.id === selectedChat.id) {
              return {
                ...chat,
                lastMessage: {
                  body: text,
                  fromMe: true,
                },
                timestamp: tempMessage.timestamp,
              };
            }
            return chat;
          });

          return updatedChats.sort((a, b) => {
            if (!a.timestamp) return 1;
            if (!b.timestamp) return -1;
            return moment(b.timestamp).diff(moment(a.timestamp));
          });
        });

        socket.emit("send-message", {
          chatId: selectedChat.id,
          message: text,
          tempId,
        });
      }
    },
    [socket, selectedChat]
  );

  // Search messages with filters
  const searchMessages = useCallback(
    (query) => {
      setSearchQuery(query);

      if (socket) {
        if (query.trim()) {
          socket.emit("searchMessages", { query });
        } else {
          socket.emit("get-chats");
        }
      }
    },
    [socket]
  );

  // Apply filter
  const applyFilter = useCallback(
    (filterType) => {
      setCurrentFilter(filterType);

      if (socket) {
        socket.emit("filterChats", {
          filterType,
          searchQuery: searchQuery,
        });
      }
    },
    [socket, searchQuery]
  );

  // Toggle chat favorite
  const toggleChatFavorite = useCallback(
    (chatId) => {
      if (socket) {
        socket.emit("toggleChatFavorite", { chatId });
      }
    },
    [socket]
  );

  // Get chat statistics
  const getChatStatistics = useCallback(() => {
    if (socket) {
      socket.emit("getChatStatistics");
    }
  }, [socket]);

  // Load more messages
  const loadMoreMessages = useCallback(
    async (chatId, offset = 0) => {
      if (!socket || isLoadingMore || !hasMoreMessages) return;

      setIsLoadingMore(true);

      try {
        socket.emit("loadMoreMessages", {
          chatId,
          offset: offset || messages[chatId]?.length || 0,
          limit: 50,
        });
      } catch (error) {
        showNotification({
          message: "Error al cargar más mensajes",
          severity: "error",
          autoHideDuration: 3000,
        });
        setIsLoadingMore(false);
      }
    },
    [socket, isLoadingMore, hasMoreMessages, messages, showNotification]
  );

  // Function to retry media loading
  const handleRetryMedia = useCallback(
    (messageId) => {
      const id = typeof messageId === "object" ? messageId.id : messageId;

      if (!socket || !selectedChat) {
        showNotification({
          message: "No se puede cargar el multimedia en este momento",
          severity: "error",
          autoHideDuration: 3000,
        });
        return;
      }

      const targetMessage = selectedChat && messages[selectedChat.id]?.find((m) => m.id === id);

      if (!targetMessage) {
        showNotification({
          message: "Mensaje no encontrado",
          severity: "error",
          autoHideDuration: 3000,
        });
        return;
      }

      setMessages((prev) => {
        const chatMessages = prev[selectedChat.id] || [];
        return {
          ...prev,
          [selectedChat.id]: chatMessages.map((msg) =>
            msg.id === id ? { ...msg, mediaStatus: "loading" } : msg
          ),
        };
      });

      socket.emit(
        "getMessageMedia",
        {
          messageId: id,
          mediaMessageRef: targetMessage.mediaMessageRef,
        },
        (response) => {
          if (!response || response.error) {
            setMessages((prev) => {
              const chatMessages = prev[selectedChat.id] || [];
              return {
                ...prev,
                [selectedChat.id]: chatMessages.map((msg) =>
                  msg.id === id
                    ? {
                        ...msg,
                        mediaStatus: "error",
                        mediaError:
                          response?.message ||
                          response?.error ||
                          "Error desconocido",
                      }
                    : msg
                ),
              };
            });
            return;
          }

          setMessages((prev) => {
            const chatMessages = prev[selectedChat.id] || [];
            return {
              ...prev,
              [selectedChat.id]: chatMessages.map((msg) =>
                msg.id === id
                  ? {
                      ...msg,
                      mediaData: response,
                      mediaStatus: "loaded",
                      mediaType:
                        response.type ||
                        getMediaTypeFromMimetype(response.mimetype),
                    }
                  : msg
              ),
            };
          });
        }
      );
    },
    [socket, selectedChat, messages, getMediaTypeFromMimetype, showNotification]
  );

  // Memoized context value to prevent re-renders
  const contextValue = useMemo(
    () => ({
      connected,
      authenticated,
      qrCode,
      chats,
      selectedChat,
      messages: selectedChat ? messages[selectedChat.id] || [] : [],
      searchQuery,
      searchResults,
      filteredChats,
      currentFilter,
      chatStatistics,
      loading,
      error,
      hasMoreMessages,
      isLoadingMore,
      selectChat,
      clearChatSelection,
      sendMessage,
      searchMessages,
      applyFilter,
      toggleChatFavorite,
      getChatStatistics,
      loadMoreMessages,
      handleRetryMedia,
      getMediaTypeFromMimetype,
      socket,
    }),
    [
      connected,
      authenticated,
      qrCode,
      chats,
      selectedChat,
      messages,
      searchQuery,
      searchResults,
      filteredChats,
      currentFilter,
      chatStatistics,
      loading,
      error,
      hasMoreMessages,
      isLoadingMore,
      selectChat,
      clearChatSelection,
      sendMessage,
      searchMessages,
      applyFilter,
      toggleChatFavorite,
      getChatStatistics,
      loadMoreMessages,
      handleRetryMedia,
      getMediaTypeFromMimetype,
      socket,
    ]
  );

  return (
    <WhatsAppContext.Provider value={contextValue}>
      {children}
    </WhatsAppContext.Provider>
  );
};

export default WhatsAppProvider;
